/**
 * The report editor: one phase of controls above the live document.
 *
 * Filters, then Versions & Sharing — never both at once, and never jammed
 * into a footer under the preview. Every control is still bound to the
 * configuration the engine is actually given; the pane below shows what
 * that produces.
 *
 * Pipeline report columns are NOT designed here — Report Studio owns
 * columns, per-column filters, grouping, totals and style. A saved pipeline
 * view opened here still exports and previews exactly as Run Now and the
 * Pipeline toolbar render it (its columns through the `viewGrid` contract),
 * never the fixed layout over its bare filters (CRE-06).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  Clock,
  Download,
  Filter,
  History,
  Loader2,
  RotateCcw,
  Save,
  Share2,
  Users,
} from 'lucide-react'
import type { Loan } from '@/types'
import { useScopedLoans } from '@/hooks/useDemoDataAccess'
import { dealScopeOf } from '@/pages/pipeline-intelligence/helpers'
import type { FilterState } from '@/pages/pipeline-intelligence/types'
import { reportsService } from '@/services/loans'
import { pipelineExportSettingsService } from '@/services/pipelineExportSettings'
import {
  exportSavedViewXlsx,
  savedViewExportPlan,
  savedViewReportFilters,
} from '@/pages/pipeline-intelligence/savedViewExport'
import { reportBrandingService } from '@/services/reportBranding'
import {
  reportDefinitionsService,
  type ReportDefinition,
} from '@/services/reportDefinitions'
import { ReportPreviewPane } from './ReportPreviewPane'
import {
  missingRequirement,
  reportTypeSpec,
  sectionsFor,
  visibleFields,
  type ReportField,
} from './reportTypes'

type Props = {
  definition: ReportDefinition
  deals: Loan[]
  onBack: () => void
  onSaved: (definition: ReportDefinition) => void
}

/** One control surface at a time, above the live document. */
type EditorPhase = 'filters' | 'versions'

const EDITOR_PHASES: Array<{
  id: EditorPhase
  label: string
  hint: string
  icon: typeof Filter
}> = [
  { id: 'filters', label: 'Filters', hint: 'Name, scope and what the document will carry', icon: Filter },
  {
    id: 'versions',
    label: 'Versions & Sharing',
    hint: 'Who edits, who can view, and the version history',
    icon: History,
  },
]

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

function filenameFrom(headers: unknown, fallback: string): string {
  const disposition = String(
    (headers as Record<string, unknown> | undefined)?.['content-disposition'] ?? '',
  )
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/)
  return match ? decodeURIComponent(match[1]) : fallback
}

export function ReportEditor({ definition, deals, onBack, onSaved }: Props) {
  const queryClient = useQueryClient()
  const spec = reportTypeSpec(definition.reportType)

  const [name, setName] = useState(definition.name)
  const [description, setDescription] = useState(definition.description)
  const [filters, setFilters] = useState<Record<string, unknown>>(
    () => ({ ...definition.filters }),
  )
  const [note, setNote] = useState('')
  const [isShared, setIsShared] = useState(definition.isShared)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [phase, setPhase] = useState<EditorPhase>('filters')

  // Re-seed when the editor is pointed at a different definition.
  useEffect(() => {
    setName(definition.name)
    setDescription(definition.description)
    setFilters({ ...definition.filters })
    setIsShared(definition.isShared)
    setNote('')
    setError(null)
    setPhase('filters')
  }, [definition.id, definition.version]) // eslint-disable-line react-hooks/exhaustive-deps

  const brandingQuery = useQuery({
    queryKey: ['report-branding'],
    queryFn: () => reportBrandingService.get(),
    staleTime: 60_000,
  })

  // Versions & Sharing is a property of this report id, not of the list row
  // that opened the editor. The ledger list is assembled across every report
  // the caller can see; an admin's larger set used to show a different history
  // than the owner (call #16). GET /reports/definitions/{id} is the same
  // payload for every reader who can open it.
  const historyQuery = useQuery({
    queryKey: ['report-definition', definition.id],
    queryFn: () => reportDefinitionsService.get(definition.id),
    staleTime: 15_000,
  })
  const history = historyQuery.data ?? definition

  const dirty =
    name !== definition.name ||
    description !== definition.description ||
    isShared !== definition.isShared ||
    JSON.stringify(filters) !== JSON.stringify(definition.filters)

  const patch = useCallback((next: Record<string, unknown>) => {
    setFilters((current) => {
      const merged = { ...current, ...next }
      // An emptied control means "not set", not "set to empty" — the engine
      // treats an absent filter as unrestricted, and a blank string would be a
      // filter matching nothing.
      for (const [key, value] of Object.entries(next)) {
        if (value === '' || value === undefined || value === null) delete merged[key]
        if (Array.isArray(value) && value.length === 0) delete merged[key]
      }
      return merged
    })
  }, [])

  const saveMutation = useMutation({
    mutationFn: () =>
      reportDefinitionsService.update(definition.id, {
        name: name.trim() || definition.name,
        description,
        filters,
        isShared,
        note,
      }),
    onSuccess: async (saved) => {
      setNotice(`Saved as version ${saved.version}.`)
      setNote('')
      await queryClient.invalidateQueries({ queryKey: ['report-definitions'] })
      await queryClient.invalidateQueries({ queryKey: ['report-definition', definition.id] })
      onSaved(saved)
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : 'The report could not be saved.'),
  })

  const restoreMutation = useMutation({
    mutationFn: (version: number) => reportDefinitionsService.restore(definition.id, version),
    onSuccess: async (saved) => {
      setNotice(`Restored. The report is now version ${saved.version}.`)
      await queryClient.invalidateQueries({ queryKey: ['report-definitions'] })
      await queryClient.invalidateQueries({ queryKey: ['report-definition', definition.id] })
      onSaved(saved)
    },
  })

  const [exporting, setExporting] = useState(false)
  const blocked = missingRequirement(spec, filters)

  // A saved pipeline view renders with its own columns, as Run Now does.
  const isPipelineView = definition.reportType === 'pipeline'
  // The deals a saved pipeline view reads. The page's own list is the live
  // pipeline; a view scoped to dead deals (or to both) would export EMPTY from it,
  // so the dead book is read when — and only when — the view's scope asks.
  const pipelineDeals = useScopedLoans(
    'reports',
    isPipelineView ? dealScopeOf(filters as Pick<FilterState, 'dealStatus'>) : 'active',
    deals,
  )
  const pipelineSettingsQuery = useQuery({
    queryKey: ['pipeline-export-settings'],
    queryFn: () => pipelineExportSettingsService.get(),
    enabled: isPipelineView,
    staleTime: 60_000,
  })
  const pipelineSettings = pipelineSettingsQuery.data
  const previewEngine = useMemo(
    () =>
      isPipelineView && pipelineSettings
        ? {
            template: pipelineSettings.template,
            filters: savedViewReportFilters(
              { name, columns: definition.columns, filters },
              pipelineDeals,
              pipelineSettings.useOnScreenColumns,
            ),
          }
        : undefined,
    [isPipelineView, pipelineSettings, name, definition.columns, filters, pipelineDeals],
  )

  const runExport = useCallback(
    async (format: 'pdf' | 'xlsx') => {
      setExporting(true)
      setError(null)
      try {
        let engine = { template: spec.engineTemplate, filters }
        if (isPipelineView) {
          const settings = await pipelineExportSettingsService.get()
          const view = { name, columns: definition.columns, filters }
          const plan = savedViewExportPlan(view, pipelineDeals, format, settings.useOnScreenColumns)
          if (plan.kind === 'workbook') {
            await exportSavedViewXlsx(view, plan.rows, (name || spec.name).toLowerCase().replace(/\s+/g, '-'))
            setNotice('Report generated and downloaded.')
            return
          }
          engine = { template: settings.template, filters: plan.filters }
        }
        const response = await reportsService.generate({
          template: engine.template,
          filters: engine.filters,
          format,
        })
        const blob = response.data as Blob
        if (!(blob instanceof Blob) || blob.size === 0) {
          throw new Error('The report engine returned an empty file.')
        }
        downloadBlob(
          blob,
          filenameFrom(
            response.headers,
            `${(name || spec.name).toLowerCase().replace(/\s+/g, '-')}.${format}`,
          ),
        )
        setNotice('Report generated and downloaded.')
        await queryClient.invalidateQueries({ queryKey: ['report-history'] })
      } catch (cause) {
        setError(await exportErrorMessage(cause))
      } finally {
        setExporting(false)
      }
    },
    [filters, name, queryClient, spec.engineTemplate, spec.name, isPipelineView, definition.columns, pipelineDeals],
  )

  const fields = visibleFields(spec, filters)
  // What THIS configuration prints — a screener's queue log is a different
  // document from its sizing memo and carries different sections.
  const sections = sectionsFor(spec, filters)
  const scope = useScopePreview(spec.id, filters, isPipelineView ? pipelineDeals : deals)

  let phaseBody: ReactNode
  switch (phase) {
    case 'filters':
      phaseBody = (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <div className="min-w-0 space-y-3">
            <div>
              <SectionLabel>Report</SectionLabel>
              <p className="mt-1 text-[11px] leading-relaxed text-stone-500">{spec.description}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Labelled label="Name" htmlFor="report-name">
                <input
                  id="report-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="reporting-input"
                />
              </Labelled>
              <Labelled label="Description" htmlFor="report-description">
                <textarea
                  id="report-description"
                  value={description}
                  rows={2}
                  onChange={(event) => setDescription(event.target.value)}
                  className="reporting-input resize-y"
                />
              </Labelled>
            </div>
            {fields.length > 0 && (
              <div>
                <SectionLabel>Scope</SectionLabel>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  {fields.map((field) => (
                    <FieldControl
                      key={field.key}
                      field={field}
                      value={filters[field.key]}
                      deals={deals}
                      loanId={(filters.loan_id as string | undefined) ?? null}
                      onChange={(value) => patch({ [field.key]: value })}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="min-w-0 rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <SectionLabel>Sections printed</SectionLabel>
            <ol className="mt-2 space-y-1">
              {sections.map((section, index) => (
                <li key={section} className="flex items-baseline gap-2 text-[11px] text-stone-300">
                  <span className="font-mono text-[9px] text-amber-300/80">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {section}
                </li>
              ))}
            </ol>
            <p className="mt-2 text-[10px] leading-relaxed text-stone-500">
              A section with no recorded content prints as “none recorded” rather than being
              omitted or filled in.
            </p>
          </div>
        </div>
      )
      break
    case 'versions':
      phaseBody = (
        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-amber-200" />
              <SectionLabel>Editor</SectionLabel>
            </div>
            <dl className="mt-2 space-y-1.5 text-[11px]">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-stone-500">Owned by</dt>
                <dd className="truncate text-stone-100" title={history.ownerEmail}>
                  {history.ownerEmail}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-stone-500">Current version</dt>
                <dd className="font-mono text-amber-100">v{history.version}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[10px] leading-relaxed text-stone-500">
              The owner is the editor. Additional named editors on this definition:
            </p>
            <p className="mt-1 truncate text-[11px] text-stone-300">
              {history.editors.length ? history.editors.join(', ') : 'Owner only'}
            </p>
          </section>

          <section className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <div className="flex items-center gap-1.5">
              <Share2 className="h-3.5 w-3.5 text-amber-200" />
              <SectionLabel>Who can view</SectionLabel>
            </div>
            <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-stone-200">
              <input
                type="checkbox"
                checked={isShared}
                onChange={(event) => setIsShared(event.target.checked)}
                className="mt-0.5 rounded border-stone-600 bg-black/40 text-amber-300 focus:ring-amber-300/40"
              />
              <span>
                Shared with the workspace
                <span className="mt-0.5 block text-[10px] leading-relaxed text-stone-500">
                  {isShared
                    ? 'Anyone who can see reports can open this one.'
                    : 'Private to the editor until shared.'}
                </span>
              </span>
            </label>
            <p className="mt-2 text-[10px] text-stone-500">Named viewers</p>
            <p className="truncate text-[11px] text-stone-300">
              {history.viewers.length
                ? history.viewers.join(', ')
                : isShared
                  ? 'Whole workspace'
                  : 'None — private to the editor'}
            </p>
          </section>

          <section className="min-w-0 rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <Labelled label="What this save is called" htmlFor="report-note">
              <input
                id="report-note"
                value={note}
                placeholder="e.g. Added the watchlist tier"
                onChange={(event) => setNote(event.target.value)}
                className="reporting-input"
              />
            </Labelled>
            <VersionHistory
              definition={history}
              onRestore={(version) => restoreMutation.mutate(version)}
              restoring={restoreMutation.isPending}
            />
          </section>
        </div>
      )
      break
    default: {
      const _exhaustive: never = phase
      phaseBody = _exhaustive
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="report-editor">
      <div className="flex shrink-0 flex-wrap items-center gap-2 pb-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[10px] text-stone-400 hover:text-amber-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Reports
        </button>
        <span className="rounded border border-amber-400/20 bg-amber-400/[0.07] px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-200">
          {spec.code}
        </span>
        <span className="text-[11px] text-stone-500">
          {spec.name} · version {definition.version}
          {dirty && <span className="ml-1 text-amber-300">· unsaved changes</span>}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {spec.formats.includes('xlsx') && (
            <button
              type="button"
              onClick={() => void runExport('xlsx')}
              aria-label="Download Excel"
              disabled={exporting || Boolean(blocked)}
              className="reporting-secondary !px-2 !py-1 text-[10px] disabled:opacity-40"
            >
              <Download className="h-3 w-3" /> Excel
            </button>
          )}
          <button
            type="button"
            onClick={() => void runExport('pdf')}
            aria-label="Download PDF"
            disabled={exporting || Boolean(blocked)}
            className="reporting-secondary !px-2 !py-1 text-[10px] disabled:opacity-40"
          >
            {exporting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            PDF
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !dirty}
            className="reporting-primary !px-2 !py-1 text-[10px] disabled:opacity-40"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Save
          </button>
        </div>
      </div>

      {(notice || error) && (
        <div
          role={error ? 'alert' : 'status'}
          className={`mb-2 shrink-0 rounded border px-3 py-1.5 text-[11px] ${
            error
              ? 'border-red-400/30 bg-red-500/10 text-red-200'
              : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'
          }`}
        >
          {error ?? notice}
        </div>
      )}

      <div
        className="flex shrink-0 items-stretch border-b border-amber-400/15"
        role="tablist"
        aria-label="Report editor phases"
      >
        {EDITOR_PHASES.map((step) => {
          const Icon = step.icon
          const selected = phase === step.id
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              aria-selected={selected}
              title={step.hint}
              onClick={() => setPhase(step.id)}
              className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-medium uppercase tracking-[0.1em] transition ${
                selected
                  ? 'border-b-2 border-amber-300 bg-amber-300/[0.05] text-amber-200'
                  : 'border-b-2 border-transparent text-stone-500 hover:text-stone-300'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {step.label}
            </button>
          )
        })}
      </div>

      <section
        className="min-h-0 shrink-0 overflow-y-auto border-b border-amber-400/10 bg-[#0d0d0d] px-3 py-3"
        data-testid={`report-editor-phase-${phase}`}
      >
        {phaseBody}
      </section>

      <div className="mt-2 flex min-h-0 flex-1 flex-col">
        <ReportPreviewPane
          spec={spec}
          name={name}
          filters={filters}
          engine={previewEngine}
          deals={deals}
          branding={brandingQuery.data}
          scopeRows={scope.rows}
          scopeLabel={scope.label}
          scopeEmptyMessage={scope.empty}
        />
      </div>
    </div>
  )
}

function VersionHistory({
  definition,
  onRestore,
  restoring,
}: {
  definition: ReportDefinition
  onRestore: (version: number) => void
  restoring: boolean
}) {
  const versions = definition.versions ?? []
  return (
    <div className="mt-3">
      <SectionLabel>Version history</SectionLabel>
      {versions.length === 0 ? (
        <p className="mt-2 text-[11px] text-stone-500">No versions recorded yet.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {versions.map((version) => (
            <li
              key={version.id}
              className="flex items-center gap-2 rounded border border-amber-400/10 bg-black/25 px-2.5 py-1.5 text-[10px]"
            >
              <span className="font-mono text-amber-300/85">v{version.version}</span>
              <span className="min-w-0 flex-1 truncate text-stone-300">{version.note}</span>
              <span className="inline-flex items-center gap-1 text-stone-500">
                <Clock className="h-3 w-3" />
                {version.createdAt
                  ? new Date(version.createdAt).toLocaleDateString()
                  : 'Not recorded'}
              </span>
              {version.version === definition.version ? (
                <span className="inline-flex items-center gap-1 text-emerald-300">
                  <Check className="h-3 w-3" /> current
                </span>
              ) : (
                <button
                  type="button"
                  disabled={restoring}
                  onClick={() => onRestore(version.version)}
                  className="inline-flex items-center gap-1 text-amber-200 hover:text-amber-100 disabled:opacity-40"
                >
                  <RotateCcw className="h-3 w-3" /> Restore
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[10px] leading-relaxed text-stone-500">
        Restoring writes a new version rather than rewriting one, so what the report looked like
        when it produced a document someone acted on stays readable.
      </p>
    </div>
  )
}

function FieldControl({
  field,
  value,
  deals,
  loanId,
  onChange,
}: {
  field: ReportField
  value: unknown
  deals: Loan[]
  /** The deal in scope — some controls are only answerable against one. */
  loanId: string | null
  onChange: (value: unknown) => void
}) {
  const id = `field-${field.key}`
  if (field.kind === 'deal') {
    return (
      <Labelled label={field.label} htmlFor={id} help={field.help}>
        <select
          id={id}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
          className="reporting-input"
        >
          <option value="">
            {deals.length ? 'Choose a deal…' : 'No accessible deals in this workspace'}
          </option>
          {deals.map((deal) => (
            <option key={deal.id} value={deal.id}>
              {deal.property} — {deal.id}
            </option>
          ))}
        </select>
      </Labelled>
    )
  }
  if (field.kind === 'select') {
    const chosen = String(value ?? field.options?.[0]?.value ?? '')
    const description = field.options?.find((option) => option.value === chosen)?.description
    return (
      <Labelled label={field.label} htmlFor={id} help={description ?? field.help}>
        <select
          id={id}
          value={chosen}
          onChange={(event) => onChange(event.target.value)}
          className="reporting-input"
        >
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Labelled>
    )
  }
  if (field.kind === 'dossierSections') {
    return <DossierSectionField field={field} value={value} onChange={onChange} loanId={loanId} />
  }
  if (field.kind === 'multiselect') {
    const chosen = Array.isArray(value) ? (value as string[]) : []
    return (
      <div>
        <span className="mb-1.5 block text-[11px] font-medium text-stone-400">{field.label}</span>
        <div className="flex flex-wrap gap-1.5">
          {(field.options ?? []).map((option) => {
            const on = chosen.includes(option.value)
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  onChange(
                    on
                      ? chosen.filter((item) => item !== option.value)
                      : [...chosen, option.value],
                  )
                }
                className={`rounded border px-2 py-1 text-[10px] ${
                  on
                    ? 'border-amber-400/40 bg-amber-400/15 text-amber-100'
                    : 'border-stone-700 text-stone-400 hover:border-amber-400/25'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        {field.help && <p className="mt-1.5 text-[10px] text-stone-500">{field.help}</p>}
      </div>
    )
  }
  return (
    <Labelled label={field.label} htmlFor={id} help={field.help}>
      <input
        id={id}
        type={field.kind === 'number' ? 'number' : field.kind === 'date' ? 'date' : 'text'}
        min={field.min}
        max={field.max}
        placeholder={field.placeholder}
        value={String(value ?? '')}
        onChange={(event) =>
          onChange(
            field.kind === 'number'
              ? event.target.value === ''
                ? ''
                : Number(event.target.value)
              : event.target.value,
          )
        }
        className="reporting-input"
      />
    </Labelled>
  )
}

/**
 * The dossier's sections, for the deal in scope.
 *
 * Fetched rather than hardcoded because availability is per deal: the server
 * knows which sections that deal actually has content for. An unavailable
 * section is still offered — the dossier prints it stating "none recorded",
 * which is the point — but it is marked, so nobody picks fourteen sections and
 * is surprised by four pages of dashes.
 */
function DossierSectionField({
  field,
  value,
  loanId,
  onChange,
}: {
  field: ReportField
  value: unknown
  loanId: string | null
  onChange: (value: unknown) => void
}) {
  const chosen = Array.isArray(value) ? (value as string[]) : []
  const optionsQuery = useQuery({
    queryKey: ['deal-package-options', loanId],
    queryFn: () => reportsService.dealPackageOptions(loanId as string),
    enabled: Boolean(loanId),
    staleTime: 60_000,
  })
  const sections = optionsQuery.data?.sections ?? []

  if (!loanId) {
    return (
      <div>
        <span className="mb-1.5 block text-[11px] font-medium text-stone-400">{field.label}</span>
        <p className="text-[10px] text-stone-500">
          Choose a deal and its section catalog loads here.
        </p>
      </div>
    )
  }
  if (optionsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-stone-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading this deal’s sections…
      </div>
    )
  }
  if (optionsQuery.isError || sections.length === 0) {
    return (
      <div>
        <span className="mb-1.5 block text-[11px] font-medium text-stone-400">{field.label}</span>
        <p className="text-[10px] text-stone-500">
          The section catalog could not be read for this deal; the dossier will print every
          section.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[11px] font-medium text-stone-400">{field.label}</span>
        <span className="text-[10px] text-stone-600">
          {chosen.length ? `${chosen.length} of ${sections.length}` : `all ${sections.length}`}
        </span>
        {chosen.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-auto text-[10px] text-amber-200 hover:text-amber-100"
          >
            Use all
          </button>
        )}
      </div>
      <div className="grid gap-1 sm:grid-cols-2">
        {sections.map((section) => {
          const on = chosen.length === 0 || chosen.includes(section.id)
          return (
            <button
              key={section.id}
              type="button"
              aria-pressed={on}
              aria-label={section.label}
              onClick={() => {
                const base = chosen.length === 0 ? sections.map((s) => s.id) : chosen
                const next = base.includes(section.id)
                  ? base.filter((id) => id !== section.id)
                  : [...base, section.id]
                onChange(next.length === sections.length ? [] : next)
              }}
              className={`flex items-center gap-2 rounded border px-2 py-1.5 text-left text-[10px] ${
                on
                  ? 'border-amber-400/35 bg-amber-400/[0.10] text-amber-100'
                  : 'border-stone-700 text-stone-500 hover:border-amber-400/25'
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{section.label}</span>
              {!section.available && (
                <span className="shrink-0 text-[9px] text-stone-500" title="Nothing recorded on this deal">
                  empty
                </span>
              )}
            </button>
          )
        })}
      </div>
      {field.help && <p className="mt-1.5 text-[10px] leading-relaxed text-stone-500">{field.help}</p>}
    </div>
  )
}

function Labelled({
  label,
  htmlFor,
  help,
  children,
}: {
  label: string
  htmlFor: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-[11px] font-medium text-stone-400">
        {label}
      </label>
      {children}
      {help && <p className="mt-1.5 text-[10px] leading-relaxed text-stone-500">{help}</p>}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300/85">
      {children}
    </div>
  )
}

/**
 * The records the report will draw on, for the preview's scope table.
 *
 * Derived from the deals already in the page's cache rather than by asking the
 * server on every keystroke: the live pane's job is to be instant, and the
 * exact pane is where the server's own answer is shown.
 */
function useScopePreview(
  reportType: string,
  filters: Record<string, unknown>,
  deals: Loan[],
): { rows: Array<{ id: string; primary: string; secondary: string; trailing: string }>; label: string; empty: string } {
  return useMemo(() => {
    // `loans.loan_amount` is NOT NULL: a package that states no amount is
    // stored as 0, served as null and read back as 0 by loansService. That 0
    // is "not stated", never a $0 request (Round 3 ruling).
    const money = (value: number | undefined) =>
      value == null || value === 0 ? '—' : `$${(value / 1_000_000).toFixed(1)}M`

    if (reportType === 'screener') {
      const variation = String(filters.variation ?? 'sizing_memo')
      if (variation === 'queue_log') {
        return {
          label: `Screened in the last ${String(filters.days ?? 30)} days`,
          rows: [],
          empty:
            'The log is drawn from intake records on the server; render the exact PDF to see it.',
        }
      }
      const deal = deals.find((candidate) => candidate.id === filters.loan_id)
      return {
        label: 'Deal screened',
        rows: deal
          ? [
              {
                id: deal.id,
                primary: deal.property,
                secondary: `${deal.type ?? '—'} · ${deal.location ?? '—'}`,
                trailing: money(deal.loanAmount),
              },
            ]
          : [],
        empty: 'No deal chosen yet.',
      }
    }

    if (reportType === 'dossier') {
      const deal = deals.find((candidate) => candidate.id === filters.loan_id)
      return {
        label: 'Deal file',
        rows: deal
          ? [
              {
                id: deal.id,
                primary: deal.property,
                secondary: `${deal.borrower ?? '—'} · ${deal.stage ?? '—'}`,
                trailing: money(deal.loanAmount),
              },
            ]
          : [],
        empty: 'No deal chosen yet.',
      }
    }

    if (reportType === 'pipeline_changes') {
      return {
        label: 'Changes in the window',
        rows: [],
        empty:
          'Changes are read from each deal’s recorded history on the server; render the exact PDF to see them.',
      }
    }

    if (reportType === 'committee_book') {
      const tabled = deals.filter((deal) => Boolean(deal.committeeMemo))
      return {
        label: 'Items tabled',
        rows: tabled.map((deal) => ({
          id: deal.id,
          primary: deal.property,
          secondary: deal.committeeMemo?.status ?? '—',
          trailing: money(deal.loanAmount),
        })),
        empty: 'No deal in this workspace carries a committee memorandum.',
      }
    }

    if (reportType === 'asset_management') {
      const funded = deals.filter((deal) => (deal.stage ?? '').toLowerCase() === 'funded')
      return {
        label: 'Funded book',
        rows: funded.map((deal) => ({
          id: deal.id,
          primary: deal.property,
          secondary: deal.type ?? '—',
          trailing: money(deal.loanAmount),
        })),
        empty: 'No funded assets are recorded in this workspace.',
      }
    }

    return {
      label: 'Records in scope',
      rows: deals.map((deal) => ({
        id: deal.id,
        primary: deal.property,
        secondary: deal.stage ?? '—',
        trailing: money(deal.loanAmount),
      })),
      empty: 'No accessible records.',
    }
  }, [deals, filters, reportType])
}

async function exportErrorMessage(cause: unknown): Promise<string> {
  const data = (cause as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string }
      if (parsed?.detail) return parsed.detail
    } catch {
      /* fall through */
    }
  } else if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  if (cause instanceof Error && cause.message) return cause.message
  return 'The report could not be generated.'
}
